import { ReactComponent as UserIcon } from "assets/images/user.svg";
import { CurrentBrand, OrganizationUser } from "modules/Types";
import { useEffect, useState } from "react";
import { Body2, Subtitle1, Subtitle2 } from "components/shareable/Typography";
import useScreen from 'Hooks/useScreen';
import useUser from 'Hooks/useUser';
import { toast } from 'react-toastify'
import { TOAST_SUCCESS_OPTIONS } from '../../Organization_constants'
import { Container } from 'modules/Sty';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import Colors from "modules/Colors";
import ModalSymphony from "elements/ModalSymphony";
import InputSymphony from "elements/InputSymphony";
import { UserPermissionOnBrand } from "modules/Const";
import { toastError } from "modules/Toast";
import ButtonSymphony from "elements/ButtonSymphony";
import AddMemberModal from "../AddMemberModal/AddMemberModal";
import { clsx, styx } from "modules/Utils";
import { CSSProperties } from "react";

interface EditBrandUserPermissionsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface UserWithPermission extends OrganizationUser {
    permissionLevel: UserPermissionOnBrand | 'none';
}

const EditBrandUserPermissionsModal = ({
    isOpen,
    onClose
}: EditBrandUserPermissionsModalProps) => {
    const { organization, organizationUsers, updateUserPermissionsOnBrand, getSelectedBrand } = useOrganizationV2();
    const { mobileView } = useScreen();
    const { currentUser } = useUser();

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [usersWithPermissions, setUsersWithPermissions] = useState<UserWithPermission[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showAddMemberModal, setShowAddMemberModal] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [initialPermissions, setInitialPermissions] = useState<UserWithPermission[]>([]);

    const selectedBrand = getSelectedBrand() as CurrentBrand;


    // Initialize users with their current brand permissions
    useEffect(() => {
        if (organizationUsers && selectedBrand && currentUser) {
            // Filter out the current user from the list
            const filteredUsers = organizationUsers

            const usersWithCurrentPermissions: UserWithPermission[] = filteredUsers.map((user: OrganizationUser) => {
                // Find the user's permission for this specific brand
                const brandPermission = user.brands?.find(brand => brand.id === selectedBrand.id);
                return {
                    ...user,
                    permissionLevel: brandPermission?.permissionLevel || 'none'
                };
            });

            setUsersWithPermissions(usersWithCurrentPermissions);
            setInitialPermissions(JSON.parse(JSON.stringify(usersWithCurrentPermissions))); // Deep copy for comparison
            setHasChanges(false);
        }
    }, [organizationUsers, selectedBrand]);

    // Don't render if no brand is selected
    if (!selectedBrand) {
        return null;
    }


    // Filter users based on search term
    const filteredUsers = usersWithPermissions.filter(user => {
        if (!searchTerm) return true;
        const fullName = `${user.name || ''} ${user.lastName || ''}`.toLowerCase();
        const email = user.email?.toLowerCase() || '';
        return fullName.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase());
    });

    const updateUserPermission = (userId: number, newPermission: UserPermissionOnBrand | 'none') => {
        setUsersWithPermissions(prev => {
            const updated = prev.map(user =>
                user.id === userId
                    ? { ...user, permissionLevel: newPermission }
                    : user
            );

            // Check if there are changes compared to initial state
            const hasChangesNow = updated.some(user => {
                const initial = initialPermissions.find(initial => initial.id === user.id);
                return initial?.permissionLevel !== user.permissionLevel;
            });

            setHasChanges(hasChangesNow);
            return updated;
        });
    };

    const setAllToAdmin = () => {
        setUsersWithPermissions(prev => {
            const updated = prev.map(user => ({ ...user, permissionLevel: UserPermissionOnBrand.ADMINISTRATOR }));
            setHasChanges(true);
            return updated;
        });
    };

    const setAllToViewOnly = () => {
        setUsersWithPermissions(prev => {
            const updated = prev.map(user => ({ ...user, permissionLevel: UserPermissionOnBrand.VIEW_ONLY }));
            setHasChanges(true);
            return updated;
        });
    };

    const removeAll = () => {
        setUsersWithPermissions(prev => {
            const updated = prev.map(user => ({ ...user, permissionLevel: 'none' as const }));
            setHasChanges(true);
            return updated;
        });
    };

    const handleSaveChanges = async () => {
        if (!organization?.id || !selectedBrand.id) return;

        try {
            setIsLoading(true);

            // Prepare the updates - only include users whose permissions have changed
            // and exclude the current user from updates
            const updates = usersWithPermissions
                .filter(user => {
                    // Exclude current user from updates
                    if (user.id === currentUser?.id) {
                        return false;
                    }
                    const initial = initialPermissions.find(initial => initial.id === user.id);
                    return initial?.permissionLevel !== user.permissionLevel;
                })
                .map(user => ({
                    userId: user.id,
                    permissionLevel: user.permissionLevel
                }));

            if (updates.length === 0) {
                toast.success('No changes to save', TOAST_SUCCESS_OPTIONS);
                onClose();
                return;
            }

            await updateUserPermissionsOnBrand({
                organizationId: organization.id,
                brandId: selectedBrand.id,
                updates
            });

            const changesCount = updates.length;
            const addedCount = updates.filter(u => u.permissionLevel !== 'none').length;
            const removedCount = updates.filter(u => u.permissionLevel === 'none').length;
            const updatedCount = changesCount - addedCount - removedCount;

            let message = `Brand permissions updated successfully! `;
            if (addedCount > 0) message += `${addedCount} user(s) added. `;
            if (removedCount > 0) message += `${removedCount} user(s) removed. `;
            if (updatedCount > 0) message += `${updatedCount} user(s) updated.`;

            toast.success(message, TOAST_SUCCESS_OPTIONS);
            onClose();
        } catch (error: any) {
            console.error('Error updating brand permissions:', error);
            toastError('Failed to update brand permissions. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const sty: Record<string, CSSProperties> = {
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            width: '100%'
        },
        searchContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            width: '100%'
        },
        bulkActionsContainer: {
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 16
        },
        userRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 12,
            backgroundColor: Colors.white,
            borderBottom: `1px solid ${Colors.greyLight}`,
        },
        userRowLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: 1,
        },
        userRowRight: {
            display: 'flex',
            alignItems: 'center',
        },
        userInfo: {
            display: 'flex',
            flexDirection: 'column',
            gap: 4
        },
        buttonActionRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            marginTop: 16,
        },
    };

    const renderDropdown = (user: UserWithPermission) => {
        console.log('user', user);
        console.log('currentUser', currentUser);
        if (user.id === currentUser?.id) {
            return (
                <div className="userRowRight" style={sty.userRowRight}>
                    <Body2 color={Colors.greyDark}>You</Body2>
                </div>
            )
        }

        return (
            <div className="userRowRight" style={sty.userRowRight}>
                <InputSymphony
                    className={clsx('userPermissionSelect', `user-${user.id}`)}
                    value={user.permissionLevel}
                    onChange={(value) => updateUserPermission(user.id, value as UserPermissionOnBrand | 'none')}
                    options={[
                        { value: UserPermissionOnBrand.ADMINISTRATOR, title: 'Admin' },
                        { value: UserPermissionOnBrand.VIEW_ONLY, title: 'View Only' },
                        { value: 'none', title: 'None' }
                    ]}
                    isSelectDropdown={true}
                    style={{ minWidth: 140 }}
                />
            </div>
        )
    }

    return (
        <>
            <ModalSymphony
                className="editBrandUserPermissionsModal"
                isOpen={isOpen}
                fullScreen={mobileView}
                onClose={handleClose}
                title={`Edit User Permissions`}
                subtitle={`These teammates have access to "${selectedBrand.name}"`}
                headerIcon={<UserIcon style={{ width: 32, height: 32 }} />}
                maxWidth={600}
            >
                <Container style={sty.container}>
                    {/* Search */}
                    <div style={sty.searchContainer}>
                        <InputSymphony
                            className="searchUsersInput"
                            type="text"
                            value={searchTerm}
                            placeholder="Search users"
                            onChange={setSearchTerm}
                            label="Search Users"
                            isSearch
                        />
                    </div>

                    {/* Bulk Actions */}
                    <div style={sty.bulkActionsContainer}>
                        <ButtonSymphony
                            className="setAllToAdminButton"
                            variant="outlined"
                            size="small"
                            onClick={setAllToAdmin}
                        >
                            Set all to Admin
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="setAllToViewOnlyButton"
                            variant="outlined"
                            size="small"
                            onClick={setAllToViewOnly}
                        >
                            Set all to View Only
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="removeAllButton"
                            variant="outlined"
                            size="small"
                            onClick={removeAll}
                        >
                            Remove all
                        </ButtonSymphony>
                    </div>

                    {/* Users List */}
                    {filteredUsers.length === 0 ? (
                        <Body2 color={Colors.greyDark}>
                            {searchTerm ? 'No users found matching your search.' : 'No users in this organization.'}
                        </Body2>
                    ) : (
                        filteredUsers.map((user) => (
                            <div key={user.id} className="userRow" style={sty.userRow}>
                                <div className="userRowLeft" style={sty.userRowLeft}>
                                    <div className="userInfo" style={sty.userInfo}>
                                        <Subtitle2>
                                            {user.name && user.lastName
                                                ? `${user.name} ${user.lastName}`
                                                : user.email
                                            }
                                        </Subtitle2>
                                        {user.name && user.lastName && (
                                            <Body2 color={Colors.greyDark}>{user.email}</Body2>
                                        )}
                                    </div>
                                </div>
                                {renderDropdown(user)}

                            </div>
                        ))
                    )}

                    {/* Action Buttons */}
                    <div className="buttonActionRow" style={sty.buttonActionRow}>
                        <ButtonSymphony
                            className="inviteNewUserButton"
                            variant="outlined"
                            onClick={() => setShowAddMemberModal(true)}
                        >
                            + Invite New User
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="buttonSaveChangesEditBrandPermissions"
                            isLoading={isLoading}
                            onClick={handleSaveChanges}
                            disabled={isLoading || !hasChanges}
                        >
                            Save Changes
                        </ButtonSymphony>
                    </div>
                </Container>
            </ModalSymphony>

            {/* Add Member Modal */}
            <AddMemberModal
                isOpen={showAddMemberModal}
                onClose={() => setShowAddMemberModal(false)}
            />
        </>
    );
};

export default EditBrandUserPermissionsModal;
